import React, { useState } from "react";
import { Box, Loader, Popover, Text, UnstyledButton } from "@mantine/core";
import { DESIGN_ICONS } from "../../../../theme/designIcons";
import type { StaffAvailability } from "./staffAvailability";
import {
  requestedFirst,
  type AssignChoice,
  type AssignMode,
} from "./assignModel";
import styles from "./AssignPicker.module.css";

const { UserPlus, Shuffle, ListEnd, Layers, ChevronLeft } = DESIGN_ICONS;

export interface AssignPickerProps {
  /** The walk-in's name ("How should we fit Maria Lopez in?"). */
  client: string;
  /**
   * Providers who perform the walk-in's service, fitted to its length
   * (`qualifiedFor`), available first.
   */
  rows: StaffAvailability[];
  /** The assign call; while its promise is pending the trigger shows a spinner. */
  onAssign: (choice: AssignChoice, mode: AssignMode) => Promise<unknown> | void;
  onOpenChange?: (open: boolean) => void;
  /** Mobile sheet: 44px rows. */
  variant?: "drawer" | "sheet";
  /**
   * The provider the client asked for (entry `preferred_staff_id`): listed
   * first, "(Requested)" and preselected; ignored when not in rows.
   */
  requestedId?: string | null;
}

/**
 * The card's "Assign staff" picker (prototype): Any available (random among
 * the available; disabled when nobody is), then each provider with
 * "Free until …" or their busy line and a right-aligned green "Available";
 * the requested provider (walk-in portal pick) first, "(Requested)" and
 * highlighted. A provider without room for the service now asks
 * "{name} is busy": After Current / Upcoming Appointment(s) (default, when a
 * gap fits today) / Start now / Pick someone else. The trigger shows a
 * spinner while the assign call runs. Mantine Popover in controlled mode
 * (`opened` + `onChange`).
 */
export default function AssignPicker({
  client,
  rows,
  onAssign,
  onOpenChange,
  variant = "drawer",
  requestedId,
}: AssignPickerProps) {
  const [opened, setOpened] = useState(false);
  const [askId, setAskId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const available = rows.filter((r) => r.available);
  const { rows: ordered, requested } = requestedFirst(rows, requestedId);
  const asking = askId ? rows.find((r) => r.id === askId) || null : null;

  const setOpen = (open: boolean) => {
    setOpened(open);
    if (!open) setAskId(null);
    onOpenChange?.(open);
  };
  const choose = (choice: AssignChoice, mode: AssignMode) => {
    setOpen(false);
    const call = onAssign(choice, mode);
    if (!call) return;
    // On success the card leaves the list; on failure the trigger re-enables.
    setPending(true);
    void Promise.resolve(call).finally(() => setPending(false));
  };

  return (
    <Popover
      opened={opened}
      onChange={setOpen}
      position="bottom-start"
      width="target"
      shadow="md"
      radius={10}
      withinPortal
      trapFocus={false}
    >
      <Popover.Target>
        <UnstyledButton
          className={styles.trigger}
          data-open={opened || undefined}
          data-variant={variant}
          aria-haspopup="listbox"
          aria-expanded={opened}
          aria-busy={pending || undefined}
          disabled={pending}
          onClick={() => setOpen(!opened)}
        >
          {pending ? <Loader size={14} color="teal" /> : <UserPlus size={15} />}
          <span>{pending ? "Assigning…" : "Assign staff"}</span>
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown className={styles.dropdown} data-variant={variant}>
        {asking ? (
          <Box className={styles.ask} data-testid="assign-busy-step">
            <Box className={styles.askHead}>
              <span className={styles.askAvatar}>{asking.initials}</span>
              <span className={styles.askText}>
                <span className={styles.askName}>{asking.name} is busy</span>
                <span className={styles.askBusy}>{asking.busyText}</span>
              </span>
            </Box>
            <Text className={styles.askQuestion}>
              How should we fit {client} in?
            </Text>
            <Box className={styles.askOptions}>
              {asking.freeAt && (
                <UnstyledButton
                  className={`${styles.option} ${styles.optionPrimary}`}
                  onClick={() => choose(asking.id, "after")}
                >
                  <ListEnd size={16} className={styles.optionIcon} />
                  <span className={styles.optionLabel}>
                    {asking.afterLabel}
                  </span>
                </UnstyledButton>
              )}
              <UnstyledButton
                className={styles.option}
                onClick={() => choose(asking.id, "now")}
              >
                <Layers size={16} className={styles.optionIcon} />
                <span className={styles.optionLabel}>Start now</span>
              </UnstyledButton>
            </Box>
            <UnstyledButton
              className={styles.back}
              onClick={() => setAskId(null)}
            >
              <ChevronLeft size={14} />
              Pick someone else
            </UnstyledButton>
          </Box>
        ) : (
          <Box role="listbox" aria-label="Assign staff">
            <UnstyledButton
              role="option"
              aria-selected={false}
              className={styles.row}
              disabled={available.length === 0}
              onClick={() => choose("any", "now")}
            >
              <span className={styles.anyIcon}>
                <Shuffle size={16} />
              </span>
              <span className={styles.rowText}>
                <span className={styles.rowName}>Any available</span>
                <span className={styles.rowSub}>
                  {available.length
                    ? `Randomly picks one of ${available.length} available`
                    : "No one is available right now"}
                </span>
              </span>
            </UnstyledButton>
            <Box className={styles.divider} />
            {rows.length === 0 && (
              <Text className={styles.none}>
                No provider offers this service.
              </Text>
            )}
            {ordered.map((r) => (
              <UnstyledButton
                key={r.id}
                role="option"
                aria-selected={r === requested}
                data-requested={r === requested || undefined}
                className={styles.row}
                onClick={() =>
                  r.available ? choose(r.id, "now") : setAskId(r.id)
                }
              >
                <span
                  className={styles.avatar}
                  data-available={r.available || undefined}
                >
                  {r.initials}
                  <span className={styles.dot} />
                </span>
                <span className={styles.rowText}>
                  <span className={styles.rowName}>
                    {r.name}
                    {r === requested && (
                      <span className={styles.requestedTag}> (Requested)</span>
                    )}
                  </span>
                  <span className={styles.rowSub}>{r.sub}</span>
                </span>
                {r.available && (
                  <span className={styles.availableTag}>Available</span>
                )}
              </UnstyledButton>
            ))}
          </Box>
        )}
      </Popover.Dropdown>
    </Popover>
  );
}
